import { AlertCircle } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { SecondaryButton } from "@/components/design-system";

/**
 * BulkAssignmentShell — canonical layout for bulk-mode assignment dialogs
 * (Kalıp / Model / Görsel / Mevcut tabloya bağla). Mirrors AssignmentDialogShell
 * in structure so single vs bulk feels like the same family, but the header
 * says "TOPLU ATAMA · N ürüne uygulanacak" and there's an inline conflict
 * banner when some of the selected products already carry a value on this
 * axis.
 *
 * Conflict banner behaviour — always rendered when `conflictCount > 0`:
 *   • "Sadece boş olanlara uygula" (mode="empty-only")  → non-destructive
 *   • "Hepsine uygula, üzerine yaz" (mode="overwrite")  → risky, marked warn
 * Caller passes both `mode` and `onModeChange` and honours the choice when
 * dispatching the assignment (the shell doesn't touch data itself).
 */
export const BulkAssignmentShell = ({
    title, count, conflictCount = 0, conflictSummary,
    mode, onModeChange,
    children, onClose, testId,
}) => (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden" data-testid={testId}>
            <DialogTitle className="sr-only">{title}</DialogTitle>
            <div className="px-5 py-4 border-b border-border">
                <div className="text-[10px] font-semibold tracking-wider uppercase text-brand">Toplu Atama</div>
                <div className="font-display font-bold text-base mt-1">{title}</div>
                <div className="text-xs text-muted-foreground mt-1">
                    <span className="font-medium text-foreground">{count}</span> ürüne uygulanacak
                </div>
            </div>
            {conflictCount > 0 && (
                <div className="px-5 py-3 border-b border-border bg-warning-soft/40" data-testid={`${testId}-conflict-banner`}>
                    <div className="flex items-start gap-2">
                        <AlertCircle className="h-3.5 w-3.5 text-warning shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium text-foreground">
                                {conflictCount} üründe zaten değer var{conflictSummary ? ` · ${conflictSummary}` : ""}
                            </div>
                            <div className="mt-2 space-y-1.5">
                                <label className="flex items-center gap-2 text-[11.5px] cursor-pointer" data-testid={`${testId}-mode-empty-only`}>
                                    <input
                                        type="radio"
                                        checked={mode === "empty-only"}
                                        onChange={() => onModeChange("empty-only")}
                                        className="h-3 w-3 accent-foreground"
                                    />
                                    <span>
                                        Sadece boş olanlara uygula ·{" "}
                                        <span className="text-foreground/80 font-medium">{count - conflictCount} ürün</span>
                                    </span>
                                </label>
                                <label className="flex items-center gap-2 text-[11.5px] cursor-pointer" data-testid={`${testId}-mode-overwrite`}>
                                    <input
                                        type="radio"
                                        checked={mode === "overwrite"}
                                        onChange={() => onModeChange("overwrite")}
                                        className="h-3 w-3 accent-foreground"
                                    />
                                    <span>
                                        Hepsine uygula, <span className="text-warning font-medium">üzerine yaz</span> ·{" "}
                                        <span className="text-foreground/80 font-medium">{count} ürün</span>
                                    </span>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <div className="max-h-80 overflow-y-auto scroll-thin bg-muted px-5 py-4 space-y-2">
                {children}
            </div>
            <div className="px-5 py-3 border-t border-border flex items-center justify-end">
                <SecondaryButton onClick={onClose} data-testid={`${testId}-close`}>
                    Kapat
                </SecondaryButton>
            </div>
        </DialogContent>
    </Dialog>
);
